import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * The browser can restore a product page from the back-forward cache with the
 * add-to-cart confirmation still open. Keep the restored page, but reset only
 * that transient confirmation state.
 */
window.addEventListener('pageshow', () => {
  const confirmation = document.querySelector('#fenza-cart-confirmation');
  if (!(confirmation instanceof HTMLElement) || confirmation.hidden) return;

  confirmation.removeAttribute('data-open');
  delete confirmation.dataset.context;
  confirmation.hidden = true;
  document.documentElement.classList.remove('fenza-cart-confirmation-open');
});

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;
  /** @type {number | undefined} */
  #mobileToastTimeout;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const eventSource = event.target instanceof Element ? event.target : null;
    const collectionQuickAddComponent =
      eventSource ? eventSource.closest('quick-add-component') : null;
    const isCollectionQuickAdd = Boolean(
      collectionQuickAddComponent?.closest('main[data-template^="collection"]')
    );
    const isProductPageAdd = eventSource && Boolean(eventSource.closest('main[data-template^="product"]'));
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !isCollectionQuickAdd &&
      !isProductPageAdd &&
      !this.#themeDrawer?.isOpen;

    if (event.action === 'add') {
      this.#themeDrawer?.setAttribute('data-cart-updating', '');
    }

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());
        const showAddedState = () => {
          this.#themeDrawer?.removeAttribute('data-cart-updating');
          this.#themeDrawer?.setAttribute('data-cart-added', '');
          window.setTimeout(() => this.#themeDrawer?.removeAttribute('data-cart-added'), 1500);
        };

        // Quantity changes and removals update the drawer, but must never reuse
        // the visual/message state reserved for a successful add operation.
        if (event.action !== 'add') {
          this.#themeDrawer?.removeAttribute('data-cart-updating');
          this.#themeDrawer?.removeAttribute('data-cart-added');
          settle();
          return;
        }

        if (detail?.didError) {
          this.#themeDrawer?.removeAttribute('data-cart-updating');
          settle();
          return;
        }

        if (isCollectionQuickAdd) {
          if (!window.matchMedia('(max-width: 749px)').matches) {
            collectionQuickAddComponent?.dispatchEvent(new CustomEvent('fenza:quick-panel-added'));
          }
          this.#showAddConfirmation(
            window.matchMedia('(max-width: 749px)').matches ? 'collection' : 'default'
          );
          showAddedState();
          settle();
          return;
        }

        if (isProductPageAdd) {
          // Product-page sticky adds use the same confirmation surface as the
          // collection quick panel. Keeping one context here makes the mobile
          // bottom sheet, its close behavior and the view-cart action identical
          // across both entry points.
          this.#showAddConfirmation(
            window.matchMedia('(max-width: 749px)').matches ? 'collection' : 'default'
          );
          showAddedState();
          settle();
          return;
        }

        if (!shouldAutoOpen) {
          showAddedState();
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          showAddedState();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        this.#themeDrawer?.removeAttribute('data-cart-updating');
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #showMobileAddToast() {
    let toast = document.querySelector('#fenza-cart-toast');
    if (!(toast instanceof HTMLElement)) {
      toast = document.createElement('div');
      toast.id = 'fenza-cart-toast';
      toast.className = 'fenza-cart-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');

      const icon = document.createElement('span');
      icon.className = 'fenza-cart-toast__icon';
      icon.setAttribute('aria-hidden', 'true');
      const cartIcon = document.querySelector('.header-actions__cart-icon svg')?.cloneNode(true);
      if (cartIcon) icon.appendChild(cartIcon);

      const message = document.createElement('span');
      message.textContent = 'ÜRÜN SEPETE EKLENDİ';
      toast.append(icon, message);
      document.body.appendChild(toast);
    }

    if (this.#mobileToastTimeout) window.clearTimeout(this.#mobileToastTimeout);
    toast.hidden = false;
    toast.removeAttribute('data-open');
    requestAnimationFrame(() => requestAnimationFrame(() => toast.setAttribute('data-open', '')));

    this.#mobileToastTimeout = window.setTimeout(() => {
      toast.removeAttribute('data-open');
      window.setTimeout(() => {
        if (!toast.hasAttribute('data-open')) toast.hidden = true;
      }, 280);
    }, 2500);
  }

  #showAddConfirmation(context = 'default') {
    const confirmation = document.querySelector('#fenza-cart-confirmation');
    if (!(confirmation instanceof HTMLElement)) return;

    confirmation.dataset.context = context;

    const configuredMessage = document
      .querySelector('results-list[data-fenza-cart-added-text]')
      ?.getAttribute('data-fenza-cart-added-text')
      ?.trim();
    const collectionResults = document.querySelector('results-list[data-fenza-cart-added-mobile-text]');
    const configuredMobileMessage = collectionResults
      ?.getAttribute('data-fenza-cart-added-mobile-text')
      ?.trim();
    const configuredMobileLink = collectionResults
      ?.getAttribute('data-fenza-cart-view-mobile-text')
      ?.trim();
    const useMobileCollectionSettings = context === 'collection' && window.matchMedia('(max-width: 749px)').matches;
    const message = confirmation.querySelector('#fenza-cart-confirmation-title');
    if (message) {
      message.textContent = useMobileCollectionSettings
        ? (configuredMobileMessage || 'Ürününüz sepete eklendi')
        : (configuredMessage || 'Ürününüz sepete eklendi');
    }
    const viewCartLink = confirmation.querySelector('.fenza-cart-confirmation__link');
    if (viewCartLink) {
      viewCartLink.textContent = useMobileCollectionSettings
        ? (configuredMobileLink || 'SEPETİ GÖRÜNTÜLE')
        : 'SEPETİ GÖRÜNTÜLE';
    }

    confirmation.hidden = false;
    confirmation.setAttribute('data-open', '');
    document.documentElement.classList.add('fenza-cart-confirmation-open');

    const escapeHandler = (event) => {
      if (event.key === 'Escape') close();
    };
    const close = () => {
      confirmation.removeAttribute('data-open');
      delete confirmation.dataset.context;
      document.documentElement.classList.remove('fenza-cart-confirmation-open');
      document.removeEventListener('keydown', escapeHandler);
      window.setTimeout(() => {
        confirmation.hidden = true;
      }, 250);
    };

    confirmation.querySelectorAll('[data-fenza-cart-confirmation-close]').forEach((button) => {
      button.addEventListener('click', close, { once: true });
    });

    document.addEventListener('keydown', escapeHandler);

    requestAnimationFrame(() => confirmation.querySelector('.fenza-cart-confirmation__panel')?.focus());
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
